import { API_URL } from '../config';

// Helper functions
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCurrentCenterId = () => {
  return localStorage.getItem('currentCenterId');
};

const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_URL}${endpoint}`;
  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    throw new Error(`Error en la petición: ${response.status}`);
  }

  return response.json();
};

export interface RelevamientoDetalleResultado {
  id: number;
  relevamientoDetalleId: number;
  checkListDetalleId?: number;
  archivoId?: number;
  checkListDetalle?: {
    id: number;
    nombre: string;
    descripcion: string;
  };
  valor: string;
  fecha: string;
  usuarioId: number;
  usuario?: {
    id: number;
    nombre?: string;
    apellido?: string;
    alias: string;
    mail: string;
  };
}

export interface RelevamientoDetalleFoto {
  id: number;
  relevamientoDetalleId: number;
  archivoId?: number;
  archivoNombre?: string;
  contentType?: string;
  sizeBytes?: number;
  url?: string;
}

export interface RelevamientoDetalle {
  id: number;
  relevamientoId: number;
  // Compat: antes usábamos descripcion, ahora Observaciones
  descripcion?: string;
  observaciones?: string;
  // Compat: antes teníamos fecha/usuario directos en el detalle; ahora los datos están en resultados, mantenemos opcionales
  fecha?: string;
  usuarioId?: number;
  usuario?: {
    id: number;
    nombre?: string;
    apellido?: string;
    alias: string;
    mail: string;
  };
  // Nuevos campos legacy
  latitud?: number;
  longitud?: number;
  productoId: number;
  puestoId?: number;
  puesto?: {
    id: number;
    nombre?: string;
  };
  archivoId?: number;
  archivo?: any;
  checkListDetalleId?: number;
  checkListDetalle?: {
    id: number;
    nombre: string;
    descripcion: string;
  };
  // Compat: nombre legacy vs nuevo
  relevamientoDetalleResultados?: RelevamientoDetalleResultado[];
  detalleResultados?: RelevamientoDetalleResultado[];
  detalleFotos?: RelevamientoDetalleFoto[];
}

export interface Relevamiento {
  id: number;
  tipoDeProductoId: number;
  tipoDeProducto?: {
    id: number;
    nombre: string;
  };
  sucursalId: number;
  sucursal?: {
    id: number;
    nombre: string;
    clienteId: number;
    cliente?: {
      id: number;
      nombre: string;
    };
  };
  tipoDeElementoId?: number;
  tipoDeElemento?: {
    id: number;
    nombre: string;
  };
  checkListId: number;
  checkList?: {
    id: number;
    nombre: string;
  };
  tareaId: number;
  tarea?: {
    id: number;
    nombre: string;
  };
  fecha: string;
  fechaFin?: string;
  leyenda?: string;
  descripcion?: string;
  fechaRecepcion?: string;
  estadoTareaId: number;
  estadoTarea?: {
    id: number;
    nombre: string;
    descripcion: string;
  };
  usuarioId: number;
  usuario?: {
    id: number;
    alias: string;
    mail: string;
  };
  remitoId?: number;
  activo: boolean;
  relevamientoDetalles?: RelevamientoDetalle[];
}

export interface RelevamientoNotificacion {
  id: number;
  leyenda?: string;
  descripcion?: string;
  fecha: string;
  fechaCreacion: string;
  estadoTareaId: number;
  estadoNombre: string;
  sucursalId: number;
  sucursalNombre?: string;
  clienteNombre?: string;
}

export const relevamientoService = {
  async getAll(): Promise<Relevamiento[]> {
    const centerId = getCurrentCenterId();
    const params = new URLSearchParams();
    params.append('pageSize', '100');
    if (centerId) params.append('centerId', centerId);
    const payload = await apiRequest(`/api/relevamientos?${params.toString()}`);
    return Array.isArray(payload) ? payload : (payload?.data ?? []);
  },

  async getAllPaginated(
    page: number = 1,
    pageSize: number = 100,
  ): Promise<{ data: Relevamiento[]; page: number; pageSize: number; hasMore: boolean }> {
    const centerId = getCurrentCenterId();
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    if (centerId) params.append('centerId', centerId);
    return apiRequest(`/api/relevamientos?${params.toString()}`);
  },

  async search(filters: {
    q?: string;
    cliente?: string;
    clienteId?: number;
    sucursal?: string;
    sucursalId?: number;
    tipoProducto?: string;
    estadoTareaId?: number;
    usuarioId?: number;
    fechaDesde?: string;
    fechaHasta?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    data: Relevamiento[];
    page: number;
    pageSize: number;
    hasMore?: boolean;
    total?: number;
    totalPages?: number;
  }> {
    const params = new URLSearchParams();

    if (filters.q) params.append('q', filters.q);
    if (filters.cliente) params.append('cliente', filters.cliente);
    if (filters.clienteId) params.append('clienteId', filters.clienteId.toString());
    if (filters.sucursal) params.append('sucursal', filters.sucursal);
    if (filters.sucursalId) params.append('sucursalId', filters.sucursalId.toString());
    if (filters.tipoProducto) params.append('tipoProducto', filters.tipoProducto);
    if (filters.estadoTareaId) params.append('estadoTareaId', filters.estadoTareaId.toString());
    if (filters.usuarioId) params.append('usuarioId', filters.usuarioId.toString());
    if (filters.fechaDesde) params.append('fechaDesde', filters.fechaDesde);
    if (filters.fechaHasta) params.append('fechaHasta', filters.fechaHasta);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

    const centerId = getCurrentCenterId();
    if (centerId) {
      params.append('centerId', centerId);
    }

    return apiRequest(`/api/relevamientos/search?${params.toString()}`);
  },

  async getById(id: number): Promise<Relevamiento> {
    const centerId = getCurrentCenterId();
    const endpoint = centerId
      ? `/api/relevamientos/${id}?centerId=${centerId}`
      : `/api/relevamientos/${id}`;
    return apiRequest(endpoint);
  },

  async getFilterOptions(): Promise<{
    clientes: { value: number; label: string }[];
    sucursales: { value: string; label: string; cliente: string }[];
    estados: { value: number; label: string }[];
    tiposProducto: { value: string; label: string }[];
  }> {
    const centerId = getCurrentCenterId();
    const endpoint = centerId
      ? `/api/relevamientos/filters?centerId=${centerId}`
      : '/api/relevamientos/filters';
    return apiRequest(endpoint);
  },

  async getByTarea(tareaId: number): Promise<Relevamiento[]> {
    return apiRequest(`/api/relevamientos/tarea/${tareaId}`);
  },

  async getByUsuario(usuarioId: number): Promise<Relevamiento[]> {
    return apiRequest(`/api/relevamientos/usuario/${usuarioId}`);
  },

  async getBySucursal(sucursalId: number): Promise<Relevamiento[]> {
    return apiRequest(`/api/relevamientos/sucursal/${sucursalId}`);
  },

  async getNotificaciones(take: number = 20): Promise<RelevamientoNotificacion[]> {
    const centerId = getCurrentCenterId();
    const params = new URLSearchParams();
    params.append('take', String(take));
    if (centerId) params.append('centerId', centerId);
    const payload = await apiRequest(`/api/relevamientos/notificaciones?${params.toString()}`);
    const rows = Array.isArray(payload) ? payload : [];
    return rows.map((r: any) => ({
      id: r.id ?? r.Id,
      leyenda: r.leyenda ?? r.Leyenda,
      descripcion: r.descripcion ?? r.Descripcion,
      fecha: r.fecha ?? r.Fecha,
      fechaCreacion: r.fechaCreacion ?? r.FechaCreacion,
      estadoTareaId: r.estadoTareaId ?? r.EstadoTareaId ?? 0,
      estadoNombre: r.estadoNombre ?? r.EstadoNombre ?? '',
      sucursalId: r.sucursalId ?? r.SucursalId ?? 0,
      sucursalNombre: r.sucursalNombre ?? r.SucursalNombre,
      clienteNombre: r.clienteNombre ?? r.ClienteNombre,
    }));
  },
};

const defaultEstadoTareaColor = 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';

/** Misma semántica que `getEstadoColor` en tareaService (estado de tarea por id o nombre). */
export const getEstadoColor = (estado: string | number | undefined | null): string => {
  if (estado === undefined || estado === null) {
    return defaultEstadoTareaColor;
  }
  if (typeof estado === 'number') {
    switch (estado) {
      case 1:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 2:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 3:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return defaultEstadoTareaColor;
    }
  }
  switch (estado.toLowerCase()) {
    case 'pendiente':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'asignada':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'finalizada':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    default:
      return defaultEstadoTareaColor;
  }
};
